/*
  <tat-predictive-search>: predictive dropdown for snippets/search-form.liquid (ARIA combobox + listbox).
  - Model numbers are matched however they are typed: TAT.resolveSearch picks the form Shopify finds ("rel ht1003" ->
    "rel ht 1003") for the predictive request and the submitted search.
  - Search shortcuts (locales/en.default.json > site > search_redirects, "term|/url" per line): a submitted query that equals
    a term (ignoring case) goes straight to that URL.
  - Keyboard: Arrow Down/Up move through the options, Enter opens the highlighted option (or submits),
    Escape closes the list (a second Escape clears the field).
  Results HTML comes from sections/predictive-search.liquid via the Section Rendering API.
*/
const TAT = window.TAT || {};
const MIN_CHARS = 2;

const resolve = async (q) => {
  const best = typeof TAT.resolveSearch === 'function' ? await TAT.resolveSearch(q) : q;
  return (best || q || '').trim();
};
const comparable = (q) => (q || '').toLowerCase().replace(/\s+/g, ' ').trim();

const parseShortcuts = (text) =>
  (text || '')
    .split(/\r?\n/)
    .map((line) => {
      const i = line.indexOf('|');
      if (i < 1) return null;
      const term = comparable(line.slice(0, i));
      const url = line.slice(i + 1).trim();
      return term && url ? { term, url } : null;
    })
    .filter(Boolean);

class TatPredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.ready) {
      document.addEventListener('click', this.onDocClick); // moved in the DOM: element listeners are still bound
      return;
    }
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="q"]');
    this.list = this.querySelector('[data-predictive-results]');
    this.status = this.querySelector('[data-predictive-status]');
    if (!this.form || !this.input) return;
    this.ready = true;

    this.shortcuts = parseShortcuts(this.dataset.redirects);
    this.cache = new Map();
    this.requestId = 0;
    this.options = [];
    this.active = -1;

    let timer;
    this.input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.search(), 180);
    });
    this.input.addEventListener('focus', () => {
      if (this.options.length && this.input.value.trim().length >= MIN_CHARS) this.open();
    });
    this.input.addEventListener('keydown', (e) => this.onKey(e));
    this.form.addEventListener('submit', (e) => this.onSubmit(e));

    if (this.list) {
      // Keep focus in the input while clicking an option (the link still navigates)
      this.list.addEventListener('mousedown', (e) => {
        if (e.target.closest('[role="option"]')) e.preventDefault();
      });
    }
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.close();
    });
    this.onDocClick = (e) => {
      if (!this.contains(e.target)) this.close();
    };
    document.addEventListener('click', this.onDocClick);
  }

  disconnectedCallback() {
    // Header re-rendered by the theme editor: don't leave the old element listening on the document.
    if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
  }

  get isOpen() {
    return this.list && !this.list.hidden;
  }

  async search() {
    const raw = this.input.value.trim();
    if (!this.list || raw.length < MIN_CHARS) {
      this.requestId++;
      this.close();
      return;
    }
    const id = ++this.requestId;
    const q = await resolve(raw);
    if (id !== this.requestId) return;

    let html = this.cache.get(q);
    if (html === undefined) {
      const base = TAT.routes?.predictiveSearch || '/search/suggest';
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,page,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        html = await TAT.fetchSection(`${base}?${params}`, 'predictive-search');
      } catch (err) {
        if (id === this.requestId) this.close();
        return;
      }
      this.cache.set(q, html);
    }
    if (id !== this.requestId) return;

    const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-content]');
    this.list.innerHTML = content ? content.innerHTML : '';
    this.options = Array.from(this.list.querySelectorAll('[role="option"]'));
    this.options.forEach((option, i) => {
      option.id = `${this.list.id}-option-${i}`;
      option.setAttribute('aria-selected', 'false');
      option.addEventListener('mousemove', () => this.highlight(i, false));
    });
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');

    const count = Number(content?.dataset.count || 0);
    if (this.status) {
      this.status.textContent = count
        ? `${count} suggestion${count === 1 ? '' : 's'} available. Use the arrow keys to browse.`
        : 'No quick matches. Press Enter to search.';
    }
    if (this.options.length) this.open();
    else this.close();
  }

  open() {
    if (!this.list) return;
    this.list.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.list) return;
    this.list.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.options.forEach((o) => o.setAttribute('aria-selected', 'false'));
    this.active = -1;
  }

  highlight(index, scroll = true) {
    this.options.forEach((o, i) => o.setAttribute('aria-selected', String(i === index)));
    this.active = index;
    const option = this.options[index];
    if (!option) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }
    this.input.setAttribute('aria-activedescendant', option.id);
    if (scroll) option.scrollIntoView({ block: 'nearest' });
  }

  onKey(e) {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!this.options.length) return;
        e.preventDefault();
        if (!this.isOpen) this.open();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        const last = this.options.length - 1;
        let next = this.active + step;
        if (next > last) next = 0;
        if (next < 0) next = last;
        this.highlight(next);
        break;
      }
      case 'Enter': {
        const option = this.isOpen ? this.options[this.active] : null;
        if (option?.href) {
          e.preventDefault();
          window.location.href = option.href;
        }
        break;
      }
      case 'Escape':
        if (this.isOpen) {
          e.preventDefault();
          this.close();
        } else if (this.input.value) {
          e.preventDefault();
          this.input.value = '';
        }
        break;
      default:
    }
  }

  onSubmit(e) {
    const raw = this.input.value.trim();
    if (!raw) {
      e.preventDefault();
      this.input.focus();
      return;
    }
    const shortcut = this.shortcuts.find((s) => s.term === comparable(raw));
    if (shortcut) {
      e.preventDefault();
      window.location.href = shortcut.url;
      return;
    }
    this.close();
    if (!TAT.searchCandidates || TAT.searchCandidates(raw).models.length === 0) return;
    // A model number: find the form Shopify matches before going to the results page
    e.preventDefault();
    const params = new URLSearchParams(new FormData(this.form));
    resolve(raw).then((q) => {
      params.set('q', q);
      window.location.href = `${this.form.getAttribute('action')}?${params}`;
    });
  }
}

if (!customElements.get('tat-predictive-search')) customElements.define('tat-predictive-search', TatPredictiveSearch);
